import React from "react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { createurls } from "../../../../services/Url.js";
import { toast } from '../../common/Toast/toast';

// Renders as a page on /setting/add-source and as the body of a dialog
// when ListSource opens it. Create only -- this form posts a batch of new
// source/target rows and has never had an edit mode.
function AddSource({
  // Shown inside a dialog rather than as a page: the card chrome around
  // the form is flattened by SettingsFormModal.css, and a Cancel appears
  // beside Reset so there is a way out that is not the close button.
  embedded = false,
  // Called after a successful save.
  onDone,
  // Called when the form is dismissed.
  onCancel,
}) {
  const initialValues = {
    rows: [
      {
        source: "",
        target: "",
        status: "ACTIVE",
      },
    ],
  };

  const validationSchema = Yup.object({
    rows: Yup.array()
      .of(
        Yup.object({
          source: Yup.string().trim().required("Source is required"),
          target: Yup.string().trim().required("Target is required"),
          status: Yup.string()
            .oneOf([ "ACTIVE", "Deactive"])
            .required("Status is required"),
        })
      )
      .min(1),
  });

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const payload = values.rows.map((row) => ({
        source: row.source.trim(),
        target: row.target.trim(),
        status: row.status,
      }));

      const response = await createurls(payload);

      toast.success(
        `${response?.data?.insertedCount || payload.length} URL(s) added successfully`
      );
      resetForm();
      if (onDone) onDone();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to add URL sources"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="card shadow-sm border-0">
        <div className="card-header text-white d-flex justify-content-between align-items-center"
        style={{ backgroundColor: "orange" }}
        >
          <h5 className="mb-0">Bulk URL Mapping</h5>
          <span className="badge bg-light text-primary">
            Add multiple sources
          </span>
        </div>

        <div className="card-body">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, isSubmitting }) => (
              <Form>
                <div className="row fw-semibold text-muted border-bottom pb-2 mb-3">
                  <div className="col-md-4">Source URL</div>
                  <div className="col-md-4">Target URL</div>
                  <div className="col-md-2">Status</div>
                  <div className="col-md-2 text-end">Action</div>
                </div>
                <FieldArray name="rows">
                  {({ push, remove }) => (
                    <>
                      {values.rows.map((_, index) => (
                        <div
                          key={index}
                          className="row align-items-start mb-3"
                        >
                          {/* SOURCE */}
                          <div className="col-md-4">
                            <Field
                              name={`rows.${index}.source`}
                              className="form-control"
                              placeholder="/google.com"
                            />
                            <ErrorMessage
                              name={`rows.${index}.source`}
                              component="div"
                              className="text-danger small"
                            />
                          </div>
                          <div className="col-md-4">
                            <Field
                              name={`rows.${index}.target`}
                              className="form-control"
                              placeholder="/courses/java-full-stack"
                            />
                            <ErrorMessage
                              name={`rows.${index}.target`}
                              component="div"
                              className="text-danger small"
                            />
                          </div>
                          <div className="col-md-2">
                            <Field
                              as="select"
                              name={`rows.${index}.status`}
                              className="form-select"
                            >
                              <option value="ACTIVE">Active</option>
                              <option value="PENDING">Deactive</option>
                            </Field>
                          </div>
                          <div className="col-md-2 text-end">
                            {values.rows.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => remove(index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      <div className="mb-4">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() =>
                            push({
                              source: "",
                              target: "",
                              status: "ACTIVE",
                            })
                          }
                        >
                          + Add Another Row
                        </button>
                      </div>
                    </>
                  )}
                </FieldArray>
                {/* FOOTER ACTIONS */}
                <div className="d-flex justify-content-end gap-2 border-top pt-3">
                  {embedded && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={isSubmitting}
                      onClick={onCancel}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="reset"
                    className="btn btn-outline-secondary"
                    disabled={isSubmitting}
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save All"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}

export default AddSource;
